import { useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@ds/core';
import type { ConversaDoBrain, ProjetoDoBrain } from '@/data/brain';
import { s } from './classes';
import { Dialogo, DialogoCabecalho, DialogoCorpo, Girador } from './ui/camadas';

/**
 * As subpáginas de lista do Brain original: "Buscar conversas", "Projetos" e o
 * diálogo de projetos (`BrainConversationsPage`, `BrainProjectsPage` e
 * `BrainProjectPicker`).
 */

function semAcento(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function dataRelativa(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const min = Math.floor((Date.now() - d.getTime()) / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${String(min)} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${String(h)}h`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

/** Foco no campo ao abrir a página — o original usava `autoFocus`. */
function useFocoInicial<T extends HTMLElement>() {
    const ref = useRef<T>(null);
    useEffect(() => {
        ref.current?.focus();
    }, []);
    return ref;
}

/* ------------------------------------------------------ buscar conversas */

export interface PaginaDeConversasProps {
    conversas: ConversaDoBrain[];
    carregando: boolean;
    ativa: string | null;
    aoAbrir: (id: string) => void;
    aoNova: () => void;
}

export function PaginaDeConversas({ conversas, carregando, ativa, aoAbrir, aoNova }: PaginaDeConversasProps) {
    const [busca, setBusca] = useState('');
    const campo = useFocoInicial<HTMLInputElement>();
    const filtradas = useMemo(() => {
        const q = semAcento(busca.trim());
        return q ? conversas.filter((c) => semAcento(c.titulo).includes(q)) : conversas;
    }, [conversas, busca]);

    return (
        <div className={s.root}>
            <div className={s.innerWide}>
                <div className={s.header}>
                    <button type="button" className={s.btnDark} onClick={aoNova}>
                        <Icon name="plus" size={14} />
                        Novo bate-papo
                    </button>
                </div>
                <div className={s.searchWrap}>
                    <span className={s.searchIcon} style={{ display: 'inline-flex' }}><Icon name="search" size={16} /></span>
                    <input
                        ref={campo} className={s.searchInput} placeholder="Pesquisar chats..." aria-label="Pesquisar conversas"
                        value={busca} onChange={(e) => setBusca(e.target.value)}
                    />
                    {busca && (
                        <button type="button" className={s.clearBtn} onClick={() => setBusca('')} aria-label="Limpar busca">
                            <Icon name="x" size={14} />
                        </button>
                    )}
                </div>
                <div className={s.list}>
                    {carregando && <div className={s.loading}><Girador tamanho={22} /></div>}
                    {!carregando && filtradas.length === 0 && (
                        <div className={s.empty}>{busca.trim() ? 'Nenhuma conversa encontrada.' : 'Nenhuma conversa ainda.'}</div>
                    )}
                    {!carregando && filtradas.map((c) => (
                        <button
                            key={c.id} type="button" className={`${s.row} ${ativa === c.id ? s.rowActive : ''}`}
                            onClick={() => aoAbrir(c.id)} aria-current={ativa === c.id ? 'true' : undefined}
                        >
                            <span className={s.rowTitle}>{c.titulo || 'Conversa sem título'}</span>
                            <span className={s.rowDate}>{dataRelativa(c.atualizadoEm)}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------- projetos */

export interface PaginaDeProjetosProps {
    projetos: ProjetoDoBrain[];
    ativo: string | null;
    carregando: boolean;
    aoEscolher: (id: string | null) => void;
    aoCriar: () => void;
}

export function PaginaDeProjetos({ projetos, ativo, carregando, aoEscolher, aoCriar }: PaginaDeProjetosProps) {
    const [busca, setBusca] = useState('');
    const filtrados = useMemo(() => {
        const q = semAcento(busca.trim());
        return q ? projetos.filter((p) => semAcento(p.nome).includes(q)) : projetos;
    }, [projetos, busca]);

    return (
        <div className={s.root}>
            <div className={s.innerWide}>
                <div className={s.header}>
                    <button type="button" className={s.btnDark} onClick={aoCriar}>
                        <Icon name="plus" size={14} />
                        Novo projeto
                    </button>
                </div>
                <div className={s.searchWrap}>
                    <span className={s.searchIcon} style={{ display: 'inline-flex' }}><Icon name="search" size={16} /></span>
                    <input className={s.searchInput} placeholder="Pesquisar projetos..." aria-label="Pesquisar projetos" value={busca} onChange={(e) => setBusca(e.target.value)} />
                    {busca && (
                        <button type="button" className={s.clearBtn} onClick={() => setBusca('')} aria-label="Limpar busca">
                            <Icon name="x" size={14} />
                        </button>
                    )}
                </div>
                {carregando && <div className={s.loading}><Girador tamanho={22} /></div>}
                {!carregando && (
                    <div className={s.grid2}>
                        {!busca.trim() && (
                            <button type="button" className={`${s.card} ${ativo === null ? s.cardActive : ''}`} onClick={() => aoEscolher(null)} aria-pressed={ativo === null}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <Icon name="messages-square" size={16} />
                                    <span className={s.cardTitle}>Todas as conversas</span>
                                </div>
                                <div className={s.cardMeta}>Sem filtro de projeto</div>
                            </button>
                        )}
                        {filtrados.map((p) => (
                            <button key={p.id} type="button" className={`${s.card} ${ativo === p.id ? s.cardActive : ''}`} onClick={() => aoEscolher(p.id)} aria-pressed={ativo === p.id}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <Icon name="folder-kanban" size={16} />
                                    <span className={s.cardTitle}>{p.nome}</span>
                                </div>
                                <div className={s.cardMeta}>{p.criadoEm ? `Criado em ${new Date(p.criadoEm).toLocaleDateString('pt-BR')}` : 'Projeto da VecchIA'}</div>
                            </button>
                        ))}
                    </div>
                )}
                {!carregando && busca.trim() && filtrados.length === 0 && (
                    <div className={s.empty}>Nenhum projeto correspondente à busca.</div>
                )}
            </div>
        </div>
    );
}

/* --------------------------------------------------- diálogo de projetos */

export interface DialogoDeProjetosProps {
    aberto: boolean;
    aoFechar: () => void;
    projetos: ProjetoDoBrain[];
    ativo: string | null;
    aoEscolher: (id: string | null) => void;
    aoCriar: (nome: string) => Promise<void>;
}

export function DialogoDeProjetos({ aberto, aoFechar, projetos, ativo, aoEscolher, aoCriar }: DialogoDeProjetosProps) {
    const [nome, setNome] = useState('');
    const [criando, setCriando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!aberto) {
            setNome('');
            setErro(null);
        }
    }, [aberto]);

    const criar = async () => {
        const limpo = nome.trim();
        if (!limpo) {
            setErro('Informe o nome do projeto.');
            return;
        }
        setCriando(true);
        setErro(null);
        try {
            await aoCriar(limpo);
            setNome('');
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Erro ao criar projeto.');
        } finally {
            setCriando(false);
        }
    };

    return (
        <Dialogo aberto={aberto} aoFechar={aoFechar} rotulo="Projetos da VecchIA">
            <DialogoCabecalho>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <h2 className={s.dialogTitle}>
                            <Icon name="sparkles" size={18} />
                            Projetos da VecchIA
                        </h2>
                        <p className={s.dialogLead}>Cada projeto agrupa conversas: a lista de recentes mostra só as do projeto ativo.</p>
                    </div>
                    <button type="button" className="brain-voice__icon-btn" onClick={aoFechar} aria-label="Fechar">
                        <Icon name="x" size={16} />
                    </button>
                </div>
            </DialogoCabecalho>
            <DialogoCorpo>
                <div className={s.grid2} style={{ maxHeight: 'min(52vh, 420px)', overflowY: 'auto' }}>
                    {projetos.map((p) => (
                        <button key={p.id} type="button" className={`${s.card} ${p.id === ativo ? s.cardActive : ''}`} onClick={() => { aoEscolher(p.id); aoFechar(); }} aria-pressed={p.id === ativo}>
                            <div className={s.cardTop}>
                                <div className={s.cardBrand}>
                                    <span className={s.cardIcon} style={{ width: 10, height: 10, borderRadius: '50%', background: '#78716c', border: 'none' }} />
                                    <span className={s.cardTitle}>{p.nome}</span>
                                </div>
                                {p.id === ativo && <Icon name="check" size={14} />}
                            </div>
                            <div className={s.cardMeta}>Conversas do projeto</div>
                        </button>
                    ))}
                </div>
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--brain-border)' }}>
                    <label htmlFor="brain-novo-projeto" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'var(--brain-text-secondary)', marginBottom: 8 }}>
                        <Icon name="folder-kanban" size={13} />
                        Novo projeto
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            id="brain-novo-projeto" className="brain-input" style={{ flex: 1 }} value={nome} maxLength={80}
                            onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Campanha de verão, Retornos…"
                            onKeyDown={(e) => { if (e.key === 'Enter') void criar(); }}
                        />
                        <button type="button" className={s.btnPrimary} onClick={() => void criar()} disabled={criando} style={{ whiteSpace: 'nowrap' }}>
                            <Icon name="plus" size={14} />
                            Criar
                        </button>
                    </div>
                    {erro && <p role="alert" style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>{erro}</p>}
                </div>
            </DialogoCorpo>
        </Dialogo>
    );
}
